'use client';

import { type FC, useCallback, useEffect, useRef } from 'react';
const PARTICLE_COLORS = ['#FFD700', '#FFA500', '#FF6B6B', '#4ECDC4', '#45B7D1', '#F9CA24', '#6C5CE7', '#FF9FF3', '#54A0FF', '#d13447', '#ffbf00', '#00D2D3'];
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  alpha: number;
  decay: number;
  gravity: number;
  shape: 'circle' | 'star' | 'rect';
  rotation: number;
  rotationSpeed: number;
}
const random = (min: number, max: number): number => Math.random() * (max - min) + min;

/**
 * @description Создаёт частицы "взрыва" из центра (звёзды, круги, прямоугольники)
 */
const createBurstParticles = (centerX: number, centerY: number): Particle[] => {
  const particles: Particle[] = [];
  const shapes: Particle['shape'][] = ['circle', 'star', 'rect'];
  for (let i = 0; i < 60; i++) {
    const angle = Math.PI * 2 * Math.random();
    const speed = random(2, 10);
    particles.push({
      x: centerX,
      y: centerY,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 3,
      size: random(3, 8),
      color: PARTICLE_COLORS[Math.floor(Math.random() * PARTICLE_COLORS.length)],
      alpha: 1,
      decay: random(0.008, 0.018),
      gravity: 0.08,
      shape: shapes[Math.floor(Math.random() * shapes.length)],
      rotation: random(0, Math.PI * 2),
      rotationSpeed: random(-0.15, 0.15)
    });
  }
  return particles;
};

/**
 * @description Создаёт конфетти, падающее сверху
 */
const createConfettiParticles = (width: number): Particle[] => {
  const particles: Particle[] = [];
  for (let i = 0; i < 30; i++) {
    particles.push({
      x: random(0, width),
      y: random(-80, -10),
      vx: random(-1, 1),
      vy: random(1.5, 4),
      size: random(4, 10),
      color: PARTICLE_COLORS[Math.floor(Math.random() * PARTICLE_COLORS.length)],
      alpha: 1,
      decay: random(0.003, 0.008),
      gravity: 0.02,
      shape: 'rect',
      rotation: random(0, Math.PI * 2),
      rotationSpeed: random(-0.1, 0.1)
    });
  }
  return particles;
};

/**
 * @description Рисует пятиконечную звезду на canvas
 */
const drawStar = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number, rotation: number): void => {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.beginPath();
  for (let i = 0; i < 5; i++) {
    const angle = i * Math.PI * 2 / 5 - Math.PI / 2;
    const innerAngle = angle + Math.PI / 5;
    const outerX = Math.cos(angle) * size;
    const outerY = Math.sin(angle) * size;
    const innerX = Math.cos(innerAngle) * size * 0.4;
    const innerY = Math.sin(innerAngle) * size * 0.4;
    if (i === 0) {
      ctx.moveTo(outerX, outerY);
    } else {
      ctx.lineTo(outerX, outerY);
    }
    ctx.lineTo(innerX, innerY);
  }
  ctx.closePath();
  ctx.fill();
  ctx.restore();
};
export const CelebrationCanvas: FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const particlesRef = useRef<Particle[]>([]);
  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const width = canvas.width / dpr;
    const height = canvas.height / dpr;
    ctx.clearRect(0, 0, width, height);
    let hasAlive = false;
    for (const p of particlesRef.current) {
      if (p.alpha <= 0) continue;
      hasAlive = true;
      p.x += p.vx;
      p.y += p.vy;
      p.vy += p.gravity;
      p.alpha -= p.decay;
      p.rotation += p.rotationSpeed;
      p.vx *= 0.99;
      if (p.alpha <= 0) continue;
      ctx.globalAlpha = Math.max(0, p.alpha);
      ctx.fillStyle = p.color;
      switch (p.shape) {
        case 'star':
          drawStar(ctx, p.x, p.y, p.size, p.rotation);
          break;
        case 'rect':
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rotation);
          ctx.fillRect(-p.size / 2, -p.size * 0.2, p.size, p.size * 0.4);
          ctx.restore();
          break;
        default:
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
    if (hasAlive) {
      animationRef.current = requestAnimationFrame(animate);
    }
  }, []);
  useEffect(() => {
    const canvas_0 = canvasRef.current;
    if (!canvas_0) return;
    const dpr_0 = window.devicePixelRatio || 1;
    const width_0 = canvas_0.offsetWidth;
    const height_0 = canvas_0.offsetHeight;
    canvas_0.width = width_0 * dpr_0;
    canvas_0.height = height_0 * dpr_0;
    const ctx_0 = canvas_0.getContext('2d');
    if (!ctx_0) return;
    ctx_0.scale(dpr_0, dpr_0);
    const centerX = width_0 / 2;
    const centerY = height_0 * 0.35;
    particlesRef.current = [...createBurstParticles(centerX, centerY), ...createConfettiParticles(width_0)];
    animationRef.current = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [animate]);
  return <div className="pointer-events-none absolute inset-0 z-10" aria-hidden="true">
      <canvas ref={canvasRef} className="h-full w-full" />
    </div>;
};